import type { HelpArticle, HelpCategory } from "@/lib/registrant-help/types";
import {
  STAFF_HELP_CATEGORIES,
  STAFF_SHARED_HELP_ARTICLES,
} from "@/lib/staff-help/shared-articles";

export const OPERATOR_HELP_ARTICLE_IDS = [
  "getting-started",
  "dashboard",
  "registrations",
  "needs-attention",
  "compliance-issues",
  "registration-details",
  "registration-upload",
  "parties",
  "party-exempt",
  "prefiling-deregistration-approval",
] as const;

export type OperatorHelpArticleId = (typeof OPERATOR_HELP_ARTICLE_IDS)[number];

export const OPERATOR_HELP_CATEGORIES: HelpCategory[] = STAFF_HELP_CATEGORIES;

const OPERATOR_PORTAL_ARTICLES: HelpArticle[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary:
      "A tour of the Hera operator portal: overview, registrations, compliance, and Help.",
    category: "getting-started",
    keywords: [
      "overview",
      "portal",
      "start",
      "home",
      "hera",
      "operator",
      "admin",
      "help",
    ],
    related: ["dashboard", "registrations", "compliance-issues"],
    sections: [
      {
        heading: "What this portal is for",
        blocks: [
          {
            type: "p",
            text: "The operator portal is where Hera Registry staff manage jurisdictions, registrations, payments, inspections, configuration, and reporting. You can flag and clear compliance issues raised by Hera, review Needs Attention, and upload registrations.",
          },
        ],
      },
      {
        heading: "Finding help",
        blocks: [
          {
            type: "p",
            text: "Use the Help icon in the header, or Help in your account menu, to open this Help Center. Contextual Help buttons open the matching article in a side panel. Help stays inside the operator portal so you remain signed in with your operator login.",
          },
        ],
      },
      {
        heading: "The main areas",
        blocks: [
          {
            type: "ul",
            items: [
              "Overview — scorecards and leadership tools.",
              "Registrations — property list with Needs Attention and compliance columns.",
              "Checks, invoices, refunds, inspections, setup, and system tools — as your role allows.",
              "Help — this help center.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "dashboard",
    title: "Overview and dashboard",
    summary:
      "Operator overview pages and how they relate to registrations and Needs Attention.",
    category: "getting-started",
    keywords: ["dashboard", "overview", "needs attention", "compliance"],
    related: ["registrations", "needs-attention", "compliance-issues"],
    sections: [
      {
        heading: "Overview",
        blocks: [
          {
            type: "p",
            text: "Overview pages summarize platform activity. Open Registrations to work individual properties, filter by Needs Attention, and open details for comments and compliance issues.",
          },
        ],
      },
      {
        heading: "Needs Attention on the list",
        blocks: [
          {
            type: "p",
            text: "The Needs Attention column includes late renewals (30 to 90 days past renewal, or 30 or more days when Historical Data Cleaned is on for that jurisdiction and license type, and the OMT is not exempt) and active compliance issues, including Ready for Check. Set the switch on the jurisdiction license type. See Needs Attention and Compliance issues.",
          },
        ],
      },
    ],
  },
];

export const OPERATOR_HELP_ARTICLES: HelpArticle[] = [
  ...OPERATOR_PORTAL_ARTICLES,
  ...STAFF_SHARED_HELP_ARTICLES,
];

const ARTICLES_BY_ID = new Map(
  OPERATOR_HELP_ARTICLES.map((article) => [article.id, article]),
);

export function getOperatorHelpArticle(id: string): HelpArticle | undefined {
  return ARTICLES_BY_ID.get(id);
}

export function isOperatorHelpArticleId(
  id: string,
): id is OperatorHelpArticleId {
  return ARTICLES_BY_ID.has(id);
}

export function operatorArticlesInCategory(
  categoryId: HelpCategory["id"],
): HelpArticle[] {
  return OPERATOR_HELP_ARTICLES.filter(
    (article) => article.category === categoryId,
  );
}
